import {Component, Output, EventEmitter, ElementRef, Input, numberAttribute, OnInit, OnDestroy, ViewChild, ChangeDetectorRef} from '@angular/core';
import {QuestionService} from "../service/question-pool/question.service";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Question} from "../model/question";
import {NgClass, NgIf, NgFor} from "@angular/common";
import {Router, RouterLink} from "@angular/router";
import { RestService } from '../service/rest/rest.service';
import { AuthService } from "../service/auth/auth.service";
import {FormControl, FormArray,Validators} from '@angular/forms';
import {DeviceDetectorService} from "ngx-device-detector";
import Hammer from 'hammerjs';
import { LoggerService } from '../service/utils/logger.service';

@Component({
  selector: 'app-question-pool-question-detail',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgClass,
    RouterLink,
    NgFor,
    //HammerModule
  ],
  templateUrl: './question-pool-question-detail.component.html',
  styleUrl: './question-pool-question-detail.component.css'
})
export class QuestionPoolQuestionDetailComponent implements OnInit{

  public questionDetailForm!: FormGroup;
  protected isSubmitted: boolean = false;
  private _questionId: number = 0;
  private _examTimeLeft: number = 1;
  protected mobile: boolean = false;
  isModalOpen = false;
  imageOverlayUrl: string | null = null;
  @Output() buttonClicked = new EventEmitter<void>();

  // When true, the component renders the question but does not allow answering
  // and avoids any navigation side effects (used by result-list detail modal).
  @Input() readOnly: boolean = false;

  @Input() shuffleQuestionAnswers: boolean = false;
  @Input() nextAutomatically: boolean = false;

  @ViewChild('questionForm') questionForm: ElementRef | undefined;

  @ViewChild('swipeArea') swipeArea!: ElementRef;
  private hammerManager!: HammerManager;


  // defines exam mode or study
  @Input() examMode: boolean = false;
  public question!: Question | null;
  // exam time left in seconds getter and setter
  @Input({required:true, transform: numberAttribute})
  set examTimeLeft(value:number){
    this._examTimeLeft = value;
    if(this.examMode && value <= 0){
      // When exam time is over, lock answering
      this.isSubmitted = true;
    }
  }
  get examTimeLeft(){
    return this._examTimeLeft;
  }
  openModal(url: string) {
    this.imageOverlayUrl = url;
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
    this.imageOverlayUrl = null;
  }

  questionImageUrlsForDisplay(): string[] {
    if (!this.question) return [];
    if (this.question.imageUrls?.length) return this.question.imageUrls;
    if (this.question.image) return [this.question.image];
    return [];
  }

  /** Returns question title with img tags stripped when we show dedicated image block(s). */
  getQuestionTitleForDisplay(): string {
    if (!this.question?.title) return '';
    if (this.questionImageUrlsForDisplay().length > 0) {
      return this.question.title.replace(/<img[^>]*>/gi, '');
    }
    return this.question.title;
  }

  // question id getter and setter
  @Input({required: true, transform: numberAttribute})
  set questionId(value: number) {
    // LoggerService.log("**" + value);
    if (this._questionId !== value) {
      this._questionId = value;
      // Clear question immediately when questionId changes to prevent showing stale data
      this.question = null;
      // Only prepare data if we have a valid questionId
      if (value && !isNaN(value)) {
        this.prepareData();
      }
    }
  }
  get questionId(): number {
    return this._questionId;
  }

  // prepares data of questions and the form
  protected async prepareData() {
    if (!this.questionId || isNaN(this.questionId)) {
      this.question = null;
      return;
    }

    this.isSubmitted = false;
    let user = this.authService.getUser();
    const sessionId = user?.sid;
    if (sessionId != null) {
      // Ensure question is in the map - wait for it to be available
      // This handles race conditions when starting a new test or resuming
      let q = await this.waitForQuestion(this.questionId, sessionId);
      if (q != null && q.loaded) {
        this.question = q;
        if (this.shuffleQuestionAnswers && this.question.answers?.length > 1) {
          this.question.answers = this.shuffleAnswers(this.question.answers);
        }
        this.initFormGroup();

        // In study mode: answered questions are locked (submitted).
        // In exam mode: answered questions remain editable (so you can reselect).
        if (q.answered) {
          this.isSubmitted = !this.examMode;
        }

        // Force change detection to ensure the view updates
        if (this.cdr) {
          this.cdr.detectChanges();
        }

        // Initialize Hammer after question is loaded and view is updated
        setTimeout(() => {
          this.initializeHammer();
        }, 0);

        return;
      } else {
        LoggerService.log("Could not fetch question " + this.questionId);
        this.question = null; // Ensure question is null if loading failed
        // In readOnly contexts (e.g. modal), do not navigate away.
        if (!this.readOnly) {
          this.router.navigate(['question-pool']);
        }
      }
    } else {
      this.question = null; // Ensure question is null if no session
    }
  }

  private initializeHammer() {
    // Clean up existing Hammer instance if any
    if (this.hammerManager) {
      this.hammerManager.off('swipe');
      this.hammerManager.destroy();
    }

    // Initialize Hammer only if swipeArea exists (question is loaded)
    if (this.swipeArea && this.swipeArea.nativeElement) {
      this.hammerManager = new Hammer(this.swipeArea.nativeElement);
      this.hammerManager.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });

      this.hammerManager.on('swipe', (event: HammerInput) => {
        if (Math.abs(event.deltaX) > 40) {
          if (event.deltaX > 0) {
            this.onSwipeLeft();
          }
          else {
            this.onSwipeRight();
          }
        }
      });
    }
  }

  /** Shuffle answer options and update option strings to match new indices. */
  private shuffleAnswers(answers: { option: string; label: string; value: number; isCorrect: boolean; isChecked: boolean; isDisabled: boolean; tip: string | null }[]): typeof answers {
    const arr = [...answers];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    arr.forEach((a, idx) => { a.option = 'answer_' + idx; });
    return arr;
  }

  private async waitForQuestion(questionId: number, sessionId: string, maxRetries: number = 10): Promise<Question | null> {
    for (let i = 0; i < maxRetries; i++) {
      let q = await this.restService.getQuestion(questionId, sessionId);
      if (q != null && q.loaded) {
        LoggerService.log(`[waitForQuestion] Successfully loaded question ${questionId} on attempt ${i + 1}`);
        return q;
      }
      // Wait before retrying (exponential backoff, but cap at 500ms)
      const waitTime = Math.min(100 * (i + 1), 500);
      LoggerService.log(`[waitForQuestion] Question ${questionId} not loaded yet, retrying in ${waitTime}ms (attempt ${i + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    LoggerService.error(`[waitForQuestion] Failed to load question ${questionId} after ${maxRetries} attempts`, null);
    return null;
  }

  initFormGroup() {
    
    this.questionDetailForm = this.fb.group({
      answers: this.fb.array([])  // Initialize as FormArray
    });

    if (this.question) {

      const formArray = this.questionDetailForm.get('answers') as FormArray;
      this.question.answers.forEach(answer => {
        // In exam mode, keep answers enabled even when already answered
        // (user can reselect a different answer).
        const isDisabled =
          this.readOnly ||
          !this.question?.canAnswer ||
          (!this.examMode && (this.isSubmitted || answer.isDisabled));
        formArray.push(this.fb.control({ value: answer.isChecked, disabled: isDisabled }));
      });
    }
  }

  get answersArray(): FormArray {
    return this.questionDetailForm.get('answers') as FormArray;
  }

  //constructor
  constructor(private fb: FormBuilder,
              private questionService: QuestionService,
              protected restService: RestService,
              private authService: AuthService,
              protected router: Router,
              private deviceService: DeviceDetectorService,
              private cdr: ChangeDetectorRef) {
                this.mobile = this.deviceService.isMobile();
  }

  // sets as submitted and sends answer to the server
  // for exam mode needed
  private sendAnswer(correct: boolean) {
    if (this.readOnly) {
      return;
    }
    console.log('[sendAnswer] Called with correct=', correct);
    // In study mode: submitting locks the question.
    // In exam mode: do NOT lock the question (user may revisit and change selection).
    this.isSubmitted = !this.examMode;
    this.buttonClicked.emit(); 

    if (this.question) {
      console.log('[sendAnswer] Question ID:', this.question.id, 'currentUserTestId:', this.restService.currentUserTestId);
      this.restService.updateAnswers(this.question, correct);
      
      // Record question as seen in user_question when answered
      console.log('[sendAnswer] Recording question as seen:', this.question.id);
      this.restService.recordQuestionSeen(this.question.id);
      
      // Record answer in background queue only if a valid option is selected
      // First try to get from answer objects (set by onChange)
      let selected = this.question.answers.find(a => a.isChecked);
      
      // If not found in answer objects, check form state (more reliable)
      if (!selected && this.questionDetailForm) {
        const formArray = this.questionDetailForm.get('answers') as FormArray;
        if (formArray) {
          for (let i = 0; i < formArray.length; i++) {
            const control = formArray.at(i);
            if (control && control.value === true) {
              // Found checked answer in form, get corresponding answer object
              selected = this.question.answers[i];
              // Sync isChecked to answer object for consistency
              if (selected) {
                selected.isChecked = true;
              }
              break;
            }
          }
        }
      }
      
      console.log('[sendAnswer] Selected answer:', selected);
      
      // Recalculate correct from the selected answer (more reliable than parameter)
      let actualCorrect = false;
      if (selected) {
        actualCorrect = selected.isCorrect === true;
        console.log('[sendAnswer] Recalculated correct from selected answer:', actualCorrect, 'isCorrect:', selected.isCorrect);
      }
      
      // Use the recalculated correct value instead of the parameter
      const finalCorrect = selected ? actualCorrect : correct;
      console.log('[sendAnswer] Using correct value:', finalCorrect, '(parameter was:', correct, ')');
      
      // Extract option letter from format "answer_0", "answer_1", etc.
      // answer_0 = A, answer_1 = B, answer_2 = C, answer_3 = D
      let option: string | null = null;
      if (selected && selected.option) {
        const match = selected.option.match(/answer_(\d)/);
        if (match) {
          const index = parseInt(match[1]);
          if (index >= 0 && index <= 3) {
            option = String.fromCharCode(65 + index); // 65 = 'A', 66 = 'B', etc.
          }
        }
      }
      console.log('[sendAnswer] Extracted option:', option, 'from', selected?.option);
      
      if (option && ['A', 'B', 'C', 'D'].includes(option)) {
        console.log('[sendAnswer] Recording answer - userTestId:', this.restService.currentUserTestId, 'questionId:', this.question.id, 'option:', option, 'correct:', finalCorrect);
        LoggerService.log('Recording answer', {
          userTestId: this.restService.currentUserTestId,
          questionId: this.question.id,
          option,
          correct: finalCorrect
        });
        this.restService.recordQuestionAnswer({
          userTestId: this.restService.currentUserTestId,
          questionId: this.question.id,
          selectedOption: option,
          correct: finalCorrect,
          examMode: this.examMode,
          answeredAt: new Date().toISOString()
        });
      } else {
        // Skip persistence if we can't derive a valid answer option
        console.warn('[sendAnswer] Skipping answer persistence - invalid option:', option, 'userTestId:', this.restService.currentUserTestId);
        LoggerService.log('Skipping answer persistence due to invalid or missing option', {
          questionId: this.question.id,
          option,
          userTestId: this.restService.currentUserTestId
        });
      }
    } else {
      console.warn('[sendAnswer] No question available');
    }

    if(this.examMode){
      // send answer to the server
      this.questionService.sendExamAnswer();
      // reset form
      this.questionDetailForm.markAsUntouched();
      this.goToNext();
      // maybe add some saving success message
    }
  }


  delay(ms: number) {
    return new Promise( resolve => setTimeout(resolve, ms) );
  }

  private goToNext(){
    const router = this.router;
    const questionIds = this.restService.getQuestionIds(); // Get limited test question IDs
    const currentQuestionId = this.question?.id;
    
    // CRITICAL: Always use questionIds array order, not question.nextId
    // The nextId from questionsMap might be stale if questionIds was modified
    
    // In exam mode, only navigate to results if ALL questions are answered
    if (this.examMode && currentQuestionId && questionIds.length > 0) {
      const questionsMap = this.restService.getQuestionsMap();
      let allAnswered = true;
      
      // Check if all questions in the test are answered
      for (const id of questionIds) {
        const q = questionsMap[id];
        if (!q || !q.answered) {
          allAnswered = false;
          break;
        }
      }
      
      // Only navigate to results if all questions are answered
      if (allAnswered) {
        const lastQuestionId = questionIds[questionIds.length - 1];
        if (currentQuestionId === lastQuestionId) {
          // Navigate to results page
          console.log('[goToNext] Exam mode - All questions answered, navigating to results');
          const userTestId = this.restService.currentUserTestId;
          if (userTestId) {
            this.delay(500).then(() => {
              router.navigate(
                ['/question-pool-results/', userTestId],
                { replaceUrl: true, state: { skyTransition: true } },
              );
            });
          } else {
            console.error('[goToNext] No userTestId available, cannot navigate to results');
          }
          return;
        }
      } else {
        // Not all questions answered, just navigate to next question
        console.log('[goToNext] Exam mode - Not all questions answered, navigating to next question');
      }
    }
    
    // Check if we're on the last question (study mode or exam mode with all answered)
    if (currentQuestionId && questionIds.length > 0) {
      const lastQuestionId = questionIds[questionIds.length - 1];
      if (currentQuestionId === lastQuestionId) {
        // In study mode: only navigate to results when ALL questions are answered (same as exam mode)
        if (!this.examMode) {
          const questionsMap = this.restService.getQuestionsMap();
          let allAnswered = true;
          for (const id of questionIds) {
            const q = questionsMap[id];
            if (!q || !q.answered) {
              allAnswered = false;
              break;
            }
          }
          if (allAnswered) {
            const userTestId = this.restService.currentUserTestId;
            if (userTestId) {
              this.delay(500).then(() => {
                router.navigate(
                  ['/question-pool-results/', userTestId],
                  { replaceUrl: true, state: { skyTransition: true } },
                );
              });
            } else {
              console.error('[goToNext] No userTestId available, cannot navigate to results');
            }
          }
          return;
        }
        // In exam mode, only navigate if all answered (checked above)
        return;
      }
    }
    
    // CRITICAL: Always find next question from questionIds array, not from question.nextId
    // This ensures we use the correct order even if questionsMap is stale
    if (currentQuestionId && questionIds.length > 0) {
      const currentIndex = questionIds.findIndex(id => Number(id) === Number(currentQuestionId));
      console.log('[goToNext] Current questionId:', currentQuestionId, 'found at index:', currentIndex, 'in questionIds array');
      
      if (currentIndex >= 0 && currentIndex < questionIds.length - 1) {
        // We're not on the last question, navigate to the next one from the array
        const nextId = questionIds[currentIndex + 1];
        console.log('[goToNext] Navigating to next question from array:', nextId, 'at index', currentIndex + 1);
        this.delay(500).then(() => {
          if (this.examMode) {
            router.navigate(['/question-pool-exam/', '' + nextId, '0'], { replaceUrl: true });
          } else {
            router.navigate(['/question-pool-study/', '' + nextId], { replaceUrl: true });
          }
        });
      } else if (currentIndex === -1) {
        console.error('[goToNext] ERROR: Current questionId', currentQuestionId, 'not found in questionIds array!');
        console.error('[goToNext] questionIds array:', questionIds);
        // Fallback: try using question.nextId if available
        const nextId = this.question?.nextId;
        if (nextId && questionIds.includes(nextId)) {
          console.log('[goToNext] Fallback: Using question.nextId:', nextId);
          this.delay(500).then(() => {
            if (this.examMode) {
              router.navigate(['/question-pool-exam/', '' + nextId, '0'], { replaceUrl: true });
            } else {
              router.navigate(['/question-pool-study/', '' + nextId], { replaceUrl: true });
            }
          });
        }
      } else {
        console.log('[goToNext] Already on last question, should have navigated to results above');
      }
    } else {
      console.error('[goToNext] No currentQuestionId or empty questionIds array');
    }
  }

  ngOnInit() {
    // Don't initialize with findFirstQuestion() - let the questionId setter handle initialization
    // This prevents race conditions when starting a new test
    // The questionId input will be set from the route, which will trigger prepareData()
    if (this.questionId && !isNaN(this.questionId)) {
      // If questionId is already set (e.g., from route), prepare data
      this.prepareData();
    }
  }

  ngAfterViewInit() {
    // Hammer initialization is now done in prepareData() after question loads
  }


  ngOnDestroy() {
    if (this.hammerManager) {
      this.hammerManager.off('swipe');
    }
  }


  onSubmit() {
  }

  // shows question and answer tip, returns false to preventDefault
  protected showTip(tip:string){
    return false;
  }

  // reacts on checkbox change and enables only one to be selected also sends answer to the server
  onChange(event:any){
    if (this.readOnly) {
      return false;
    }
    // Prevent changing answer if question is already answered
    if (this.question?.answered && !this.examMode) {
      console.log('[onChange] Question already answered, ignoring change');
      // Restore the checked state to prevent UI changes
      if (this.question) {
        this.question.answers.forEach(answer => {
          const control = this.answersArray.at(this.question!.answers.indexOf(answer));
          if (control) {
            control.setValue(answer.isChecked, { emitEvent: false });
          }
        });
      }
      return false;
    }

    // In exam mode, don't allow "unselecting" the currently selected answer to produce "no answer".
    // Treat checkboxes like radios: you can reselect another answer, but always keep one selected.
    if (this.examMode && event?.target && event.target.checked === false) {
      const id = event.target.id;
      const idx = typeof id === 'string' ? Number.parseInt(id.replace('answer_', ''), 10) : NaN;
      if (!Number.isNaN(idx)) {
        const control = this.answersArray.at(idx);
        control?.setValue(true, { emitEvent: false });
      }
      return false;
    }
    
    // LoggerService.log(event);
    this.isSubmitted = !this.examMode;
    this.updateDisabledState();

    let correct = false;
    // make only one answer selectable
    let q = this.question;
    if (q != null) {
      q.answers.forEach((answer, idx) => {

        if (event.target.id == answer.option) {
          answer.isChecked = true;
          console.log('[onChange] Set answer', answer.option, 'isChecked=true, isCorrect=', answer.isCorrect);
        } else {
          answer.isChecked = false;
        }

        // In study mode we lock the other answers after selection.
        // In exam mode we keep other answers enabled so the user can reselect later.
        if (!this.examMode) {
          if (event?.target?.checked) {
            if (event.target.id != answer.option) {
              answer.isDisabled = true;
            }
          } else {
            answer.isDisabled = false;
          }
        } else {
          answer.isDisabled = false;
        }

        // Check if it is correct
        if (answer.isChecked && answer.isCorrect) {
          correct = true;
          console.log('[onChange] Found correct answer:', answer.option, 'correct set to true');
        }

        // Keep form state in sync with our single-selection model
        const control = this.answersArray.at(idx);
        control?.setValue(answer.isChecked, { emitEvent: false });
      });
      q.answered = true;
      q.correctAnswer = correct;
      console.log('[onChange] Final correct value:', correct, 'for question', q.id);
    }

    // send answer to the server
    this.sendAnswer(correct);

    // go to next question if answer was correct and user has enabled auto-advance (study mode only)
    // Use @Input value or fall back to RestService cache (in case parent binding was stale, e.g. after settings changed in another tab)
    const nextAuto = this.nextAutomatically || this.restService.getCachedUserTestSettings()?.nextAutomatically === true;
    if (correct && !this.examMode) {
      if (nextAuto) {
        console.log('[onChange] Correct answer, nextAutomatically=true, calling goToNext()');
        this.goToNext();
      } else {
        console.log('[onChange] Correct answer but nextAutomatically=false (input:', this.nextAutomatically, ', cache:', this.restService.getCachedUserTestSettings()?.nextAutomatically, '), not auto-advancing');
      }
    }

    return false;
  }


  updateDisabledState() {
    const formArray = this.answersArray;
    if (this.question) {
      formArray.controls.forEach((control:any, index:any) => {
        const answer = this.question!.answers[index];
        // Study mode: lock answered questions.
        // Exam mode: keep answered questions editable.
        const isDisabled =
          this.readOnly ||
          !this.question?.canAnswer ||
          (!this.examMode && (this.isSubmitted || answer.isDisabled || this.question.answered));
        isDisabled ? control.disable() : control.enable();
      });
    }
  }


  // Handle swipe left event (navigate to the next question)
  onSwipeLeft() {
    if (this.readOnly) return;
    if (this.question && this.question.prevId !== null) {
      const prevId = this.question.prevId;
      const link = this.examMode
        ? ['/question-pool-exam/', prevId.toString(), '0']
        : ['/question-pool-study/', prevId.toString()];
      this.router.navigate(link);
    }
  }

  // Handle swipe right event (navigate to the previous question)
  onSwipeRight() {
    if (this.readOnly) return;
    if (this.question && this.question.nextId !== null) {
      const nextId = this.question.nextId;
      const link = this.examMode
        ? ['/question-pool-exam/', nextId.toString(), '0']
        : ['/question-pool-study/', nextId.toString()];
      this.router.navigate(link);
    }
  }
}
